/**
 * 卓の会計に、時間制の席料を足すための入口。
 *
 * 画面ごとに「入店から何分か」を数えて掛け算を書くと、
 * レジ・卓の詳細・伝票の印刷で違う金額が出る。
 * 数えるのも掛けるのも、ここ1箇所だけにする。
 *
 * アプリ側は shop_staff_app_v2/lib/utils/session_charge.dart が同じことをする。
 * scripts/check-time-charge.mjs が両方を突き合わせている。
 */
import { computeTimeCharge, type TimeChargeResult, type TimeChargeSettings } from './timeCharge';

export interface SessionChargeInput {
  /** 入店した時刻 */
  startTime: Date;
  /** 会計する時刻（まだ会計していなければ今） */
  endTime: Date;
  /** 営業日の区切り（時）。6 なら朝6時で日が変わる。 */
  businessDayBoundaryHour: number;
}

export interface SessionCharge {
  applies: boolean;
  stayMinutes: number;
  result: TimeChargeResult | null;
  /** 計算できなかった理由。画面に出して、黙って0円にしない。 */
  error: string | null;
  /** 席料の合計（税抜・サービス料込み・端数切り上げ済み） */
  amount: number;
}

/**
 * 入店時刻を「営業日の0時からの分」に直す。
 *
 * 区切りが朝6時の店で 26:30（＝翌2:30）に入店した客は、
 * その営業日の 1590 分として扱う。暦の日で切ると
 * 深夜の客だけ別の日になり、時間帯の単価が引けなくなる。
 */
export function minuteOfBusinessDay(at: Date, boundaryHour: number): number {
  const m = at.getHours() * 60 + at.getMinutes();
  return at.getHours() < boundaryHour ? m + 24 * 60 : m;
}

/**
 * 卓の席料を出す。
 *
 * 設定が無い店（時間制を使わない店）は applies=false を返す。
 * その場合は席料を足さない。0円の席料を足すのとは意味が違う。
 */
export function computeSessionCharge(
  settings: TimeChargeSettings | null | undefined,
  input: SessionChargeInput,
): SessionCharge {
  const usesTimeCharge = !!settings &&
    (settings.bands.length > 0 || settings.flatSetPrice > 0 || settings.flatExtensionPrice > 0);
  if (!usesTimeCharge) {
    return { applies: false, stayMinutes: 0, result: null, error: null, amount: 0 };
  }

  const stay = Math.floor((input.endTime.getTime() - input.startTime.getTime()) / 60000);
  if (stay < 0) {
    // 時計がずれている、または入店時刻が未来。黙って0にしない。
    return {
      applies: true, stayMinutes: 0, result: null, amount: 0,
      error: '入店の時刻が会計の時刻より後になっています',
    };
  }

  try {
    const start = minuteOfBusinessDay(input.startTime, input.businessDayBoundaryHour);
    const result = computeTimeCharge(settings!, start, stay);
    return { applies: true, stayMinutes: stay, result, error: null, amount: result.beforeTax };
  } catch (e: any) {
    // 単価の決まっていない時間帯など。会計を通さず理由を出す。
    return {
      applies: true, stayMinutes: stay, result: null, amount: 0,
      error: String(e?.message ?? e),
    };
  }
}
